/**
 * CookieConsentBanner — ADR-4 consent banner for the customer portal.
 *
 * gigmosaic-fe ships no banner today (see `ai-work/docs/tasks/telemetry/
 * decisions.md`, Decision 5), so this is a NEW non-blocking banner:
 *
 *  - analytics is ON by default (opt-out regime)
 *  - an explicit analytics on/off toggle (separate from marketing)
 *  - persists to the `hp_consent` cookie — the exact cookie the
 *    `@aplicy-com/hp-telemetry-web` SDK reads in `src/consent.js` — together
 *    with the ADR-4 consent VERSION
 *  - a "Privacy choices" link (see `openPrivacyChoices`) re-opens it from the
 *    footer
 *
 * Persisting `version` requires writing the cookie directly: the installed
 * SDK (0.1.1) `setConsent()` drops the version field. See
 * `src/lib/telemetry.ts` for the rationale.
 */
import { useEffect, useState } from 'react';
import {
  readConsent,
  writeConsent,
  TELEMETRY_CONSENT_VERSION,
} from '../lib/telemetry';
import { OPEN_PRIVACY_CHOICES_EVENT } from './privacyChoices';

const DISMISSED_KEY = 'hp_privacy_banner_dismissed';

function isDismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

function dismiss(): void {
  try {
    window.localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function CookieConsentBanner() {
  const [open, setOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  // Initialise from the persisted consent + re-prompt when the consent
  // version changes (banner text updated → user must re-confirm).
  useEffect(() => {
    const initial = readConsent();
    setAnalytics(initial.analytics !== false);
    setMarketing(initial.marketing === true);
    setOpen(initial.version !== TELEMETRY_CONSENT_VERSION || !isDismissed());
  }, []);

  // Listen for the footer "Privacy choices" link.
  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(OPEN_PRIVACY_CHOICES_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PRIVACY_CHOICES_EVENT, onOpen);
  }, []);

  const persist = (a: boolean, m: boolean) => {
    writeConsent({
      analytics: a,
      marketing: m,
      version: TELEMETRY_CONSENT_VERSION,
    });
  };

  const handleToggleAnalytics = () => {
    const next = !analytics;
    setAnalytics(next);
    persist(next, marketing);
  };

  const handleToggleMarketing = () => {
    const next = !marketing;
    setMarketing(next);
    persist(analytics, next);
  };

  const handleSave = () => {
    persist(analytics, marketing);
    dismiss();
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      role="region"
      aria-label="Privacy preferences"
      className="fixed inset-x-0 bottom-0 z-[100] bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]"
    >
      <div className="mx-auto max-w-7xl px-4 py-4 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">
            Your privacy choices
          </p>
          <p className="text-xs text-gray-600 mt-1 max-w-3xl">
            We use analytics to understand how the site is used and improve it.
            Analytics is on by default and can be turned off at any time. See
            our{' '}
            <a href="/privacy-policy" className="underline hover:text-primary">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/cookie-policy" className="underline hover:text-primary">
              Cookie Policy
            </a>
            .
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            <span>Analytics</span>
            <input
              type="checkbox"
              checked={analytics}
              onChange={handleToggleAnalytics}
              className="w-4 h-4 accent-primary"
              aria-label="Allow analytics cookies"
            />
          </label>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
            <span>Marketing</span>
            <input
              type="checkbox"
              checked={marketing}
              onChange={handleToggleMarketing}
              className="w-4 h-4 accent-primary"
              aria-label="Allow marketing cookies"
            />
          </label>
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90"
          >
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsentBanner;
